import { useEffect, useState } from 'react';
import PortalSidebar from '../components/PortalSidebar';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

export default function ProfileSettingsPage() {
  const [tab, setTab] = useState('personal');
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({});
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '' });
  const [notifPrefs, setNotifPrefs] = useState({ statusUpdates: true, jobMatches: false, newsletter: true });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetch(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((json) => {
        if (json.success) {
          setUser(json.data);
          setForm({
            fullName: json.data.fullName || '',
            email: json.data.email || '',
            phone: json.data.phone || '',
            nationality: json.data.nationality || '',
            address: json.data.address || '',
          });
        }
      });
  }, [token]);

  const handleSaveProfile = async () => {
    setSaving(true);
    setMessage('');
    const res = await fetch(`${API_BASE}/portal/profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(form),
    });
    const json = await res.json();
    setSaving(false);
    setMessage(json.success ? 'Profile updated successfully.' : json.error?.message || 'Update failed.');
  };

  const handleChangePassword = async () => {
    setSaving(true);
    setMessage('');
    const res = await fetch(`${API_BASE}/portal/profile/password`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ currentPassword: passwordForm.current, newPassword: passwordForm.next }),
    });
    const json = await res.json();
    setSaving(false);
    setMessage(json.success ? 'Password changed successfully.' : json.error?.message || 'Password change failed.');
    if (json.success) setPasswordForm({ current: '', next: '' });
  };

  const handleAvatarChange = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('avatar', file);
    const res = await fetch(`${API_BASE}/portal/profile/avatar`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    const json = await res.json();
    if (json.success) setUser({ ...user, avatarUrl: json.data.avatarUrl });
  };

  const toggleNotif = (key) => setNotifPrefs({ ...notifPrefs, [key]: !notifPrefs[key] });

  if (!user) return <p className="text-center py-40 text-[#64748b]">Loading...</p>;

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <PortalSidebar />
      <main className="md:ml-64 flex-1 p-6 md:p-12">
        <div className="max-w-4xl mx-auto">
          <header className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <h2 className="text-3xl font-bold text-[#00255a] mb-2">Account Settings</h2>
              <p className="text-[#44474f] max-w-lg">
                Manage your personal information, security credentials, and platform preferences.
              </p>
            </div>
            <div className="relative group">
              <div className="w-24 h-24 rounded-full border-4 border-white shadow-lg overflow-hidden bg-[#8fb3ff] flex items-center justify-center">
                {user.avatarUrl ? (
                  <img className="w-full h-full object-cover" src={user.avatarUrl} alt={user.fullName} />
                ) : (
                  <span className="text-white text-3xl font-bold">{user.fullName?.charAt(0)}</span>
                )}
              </div>
              <label className="absolute -bottom-1 -right-1 bg-white p-1.5 rounded-full shadow-md text-[#00255a] cursor-pointer">
                <span className="material-symbols-outlined text-sm">photo_camera</span>
                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleAvatarChange(e.target.files[0])} />
              </label>
            </div>
          </header>

          {message && <div className="mb-6 p-3 bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg">{message}</div>}

          <div className="flex border-b border-[#e2e8f0] mb-8 overflow-x-auto">
            {['personal', 'security', 'notifications'].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-6 py-4 font-semibold whitespace-nowrap transition-all ${
                  tab === t ? 'text-[#00255a] border-b-2 border-[#00255a]' : 'text-[#44474f] hover:text-[#00255a]'
                }`}
              >
                {t === 'personal' ? 'Personal Info' : t === 'security' ? 'Security' : 'Notification Preferences'}
              </button>
            ))}
          </div>

          {tab === 'personal' && (
            <section className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl border border-[#e2e8f0] shadow-sm">
                  <h3 className="text-lg font-bold text-[#00255a] mb-6 flex items-center gap-2">
                    <span className="material-symbols-outlined">person</span> Identity
                  </h3>
                  <div className="space-y-6">
                    <Field label="Full Legal Name" value={form.fullName} onChange={(v) => setForm({ ...form, fullName: v })} />
                    <Field label="Nationality" value={form.nationality} onChange={(v) => setForm({ ...form, nationality: v })} />
                  </div>
                </div>
                <div className="bg-white p-6 rounded-xl border border-[#e2e8f0] shadow-sm">
                  <h3 className="text-lg font-bold text-[#00255a] mb-6 flex items-center gap-2">
                    <span className="material-symbols-outlined">contact_mail</span> Contact Details
                  </h3>
                  <div className="space-y-6">
                    <Field label="Email Address" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                    <Field label="Phone Number" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-xl border border-[#e2e8f0] shadow-sm">
                <h3 className="text-lg font-bold text-[#00255a] mb-6 flex items-center gap-2">
                  <span className="material-symbols-outlined">home</span> Address
                </h3>
                <Field label="Home Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
              </div>
            </section>
          )}

          {tab === 'security' && (
            <section className="space-y-8">
              <div className="bg-white p-8 rounded-xl border border-[#e2e8f0] shadow-sm">
                <h3 className="text-lg font-bold text-[#00255a] mb-8 flex items-center gap-2">
                  <span className="material-symbols-outlined">shield</span> Access &amp; Authentication
                </h3>
                <div className="space-y-6">
                  <Field label="Current Password" type="password" value={passwordForm.current} onChange={(v) => setPasswordForm({ ...passwordForm, current: v })} />
                  <Field label="New Password" type="password" value={passwordForm.next} onChange={(v) => setPasswordForm({ ...passwordForm, next: v })} />
                  <button
                    onClick={handleChangePassword}
                    disabled={saving || !passwordForm.current || !passwordForm.next}
                    className="px-6 py-3 bg-[#00255a] text-white rounded-lg font-semibold disabled:opacity-50"
                  >
                    Update Password
                  </button>
                </div>
              </div>
            </section>
          )}

          {tab === 'notifications' && (
            <section className="space-y-8">
              <div className="bg-white p-8 rounded-xl border border-[#e2e8f0] shadow-sm">
                <h3 className="text-lg font-bold text-[#00255a] mb-8 flex items-center gap-2">
                  <span className="material-symbols-outlined">notifications_active</span> Alerts &amp; Subscriptions
                </h3>
                <div className="space-y-6">
                  <NotifRow
                    title="Application Status Updates"
                    desc="Get notified immediately when your application status changes."
                    checked={notifPrefs.statusUpdates}
                    onToggle={() => toggleNotif('statusUpdates')}
                  />
                  <NotifRow
                    title="Job Opportunity Matches"
                    desc="Digest of curated positions based on your study field."
                    checked={notifPrefs.jobMatches}
                    onToggle={() => toggleNotif('jobMatches')}
                  />
                  <NotifRow
                    title="Newsletter & Tips"
                    desc="Periodic expert advice on international relocation and visa success."
                    checked={notifPrefs.newsletter}
                    onToggle={() => toggleNotif('newsletter')}
                  />
                </div>
              </div>
            </section>
          )}

          <footer className="mt-12 flex items-center justify-between pt-8 border-t border-[#e2e8f0]">
            <button className="px-6 py-3 text-[#44474f] hover:text-[#00255a] transition-colors flex items-center gap-2">
              <span className="material-symbols-outlined">refresh</span> Discard Changes
            </button>
            {tab === 'personal' && (
              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="px-10 py-4 text-white font-bold rounded-lg shadow-xl hover:scale-105 transition-all flex items-center gap-2 disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
              >
                {saving ? 'Saving...' : 'Save Account Changes'}
                <span className="material-symbols-outlined">check_circle</span>
              </button>
            )}
          </footer>
        </div>
      </main>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[#44474f] mb-1">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 bg-[#f2f4f6] border border-[#e2e8f0] rounded-lg focus:ring-2 focus:ring-[#00255a]/10 focus:border-[#00255a] outline-none transition-all"
      />
    </div>
  );
}

function NotifRow({ title, desc, checked, onToggle }) {
  return (
    <div className="flex items-center justify-between py-4 border-b border-[#e2e8f0] last:border-0">
      <div>
        <p className="font-semibold text-[#00255a]">{title}</p>
        <p className="text-sm text-[#64748b]">{desc}</p>
      </div>
      <button
        onClick={onToggle}
        className={`w-14 h-7 rounded-full relative transition-colors ${checked ? 'bg-[#00255a]' : 'bg-[#e0e3e5]'}`}
      >
        <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${checked ? 'translate-x-8' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}